import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getSaasToken } from '../../auth';
import type { Employee } from '../../types';
import { Card, Tag } from '../../components/ds';

interface Branch {
  id: string;
  code: string;
  name: string;
  region: string;
  isHeadOffice: boolean;
}

interface PayrollRun {
  id: string;
  code: string;
  title: string;
  periodStart: string;
  periodEnd: string;
  payoutDate: string;
  runType: string;
  status: string;
  totalGross: string;
  totalTaxable: string;
  totalWithholdingTax: string;
  totalSssEe: string;
  totalSssEr: string;
  totalPhilhealthEe: string;
  totalPhilhealthEr: string;
  totalPagibigEe: string;
  totalPagibigEr: string;
  totalNetPay: string;
  employeeCount: number;
  branchName: string | null;
  createdAt: string;
}

interface PayrollLineItem {
  id: string;
  employee_id: string;
  employeeNo: string;
  firstName: string;
  lastName: string;
  branchName: string | null;
  basic_pay: string;
  de_minimis_allowances: string;
  taxable_allowances: string;
  gross_pay: string;
  sss_ee: string;
  philhealth_ee: string;
  pagibig_ee: string;
  taxable_income: string;
  withholding_tax: string;
  total_deductions: string;
  net_pay: string;
  thirteenth_month_accrual: string;
  disbursement_status: string;
  bank_name: string;
  bank_account_no: string;
}

interface PayrollDetail {
  run: PayrollRun;
  lineItems: PayrollLineItem[];
}

export default function PayrollDashboard() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Employee>('/employees/me') });
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'run' | 'statutory' | 'disbursement'>('run');
  const [showComputeModal, setShowComputeModal] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  // Form state for Compute Run Modal
  const [computeForm, setComputeForm] = useState({
    code: `PAY-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-Q1`,
    title: `${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })} 1st Quindena Payroll`,
    periodStart: new Date().toISOString().slice(0, 8) + '01',
    periodEnd: new Date().toISOString().slice(0, 8) + '15',
    payoutDate: new Date().toISOString().slice(0, 8) + '15',
    runType: 'quindena_1',
    branchId: '',
  });

  const phPayrollEnabled = me.data?.features?.feature_ph_payroll ?? false;

  // 1. Fetch Branches
  const branchesQuery = useQuery({
    queryKey: ['payroll-branches'],
    queryFn: () => api<Branch[]>('/payroll/branches'),
    enabled: phPayrollEnabled,
  });

  // 2. Fetch Payroll Runs
  const runsQuery = useQuery({
    queryKey: ['payroll-runs', selectedBranch],
    queryFn: () =>
      api<PayrollRun[]>(
        `/payroll/runs${selectedBranch !== 'all' ? `?branchId=${selectedBranch}` : ''}`,
      ),
    enabled: phPayrollEnabled,
  });

  // Set activeRunId to first run if not set
  const runs = runsQuery.data || [];
  const currentRunId = activeRunId || (runs[0]?.id ?? null);

  // 3. Fetch Selected Payroll Run Detail & Line Items
  const runDetailQuery = useQuery({
    queryKey: ['payroll-run', currentRunId],
    queryFn: () => api<PayrollDetail>(`/payroll/runs/${currentRunId}`),
    enabled: phPayrollEnabled && Boolean(currentRunId),
  });

  // Mutations
  const computeMutation = useMutation({
    mutationFn: (payload: typeof computeForm) =>
      api<PayrollRun>('/payroll/runs/compute', {
        method: 'POST',
        body: {
          ...payload,
          branchId: payload.branchId || null,
        },
      }),
    onSuccess: (newRun) => {
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      setActiveRunId(newRun.id);
      setShowComputeModal(false);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (runId: string) =>
      api<PayrollRun>(`/payroll/runs/${runId}/approve`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll-run', currentRunId] });
    },
  });

  const disburseMutation = useMutation({
    mutationFn: (runId: string) =>
      api<PayrollRun>(`/payroll/runs/${runId}/disburse`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll-run', currentRunId] });
    },
  });

  // Download helpers
  const downloadDisbursement = async (bank: string) => {
    if (!currentRunId) return;
    try {
      const token = getSaasToken();
      const response = await fetch(`/api/payroll/runs/${currentRunId}/disbursement/${bank.toLowerCase()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Disbursement_${bank.toUpperCase()}_${currentRunId.slice(0, 8)}.csv`;
      a.click();
    } catch (e) {
      console.error('Failed to download disbursement file', e);
    }
  };

  const downloadStatutory = async (agency: string) => {
    if (!currentRunId) return;
    try {
      const token = getSaasToken();
      const response = await fetch(`/api/payroll/runs/${currentRunId}/statutory/${agency.toLowerCase()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${agency.toUpperCase()}_Report_${currentRunId.slice(0, 8)}.csv`;
      a.click();
    } catch (e) {
      console.error('Failed to download statutory report', e);
    }
  };

  if (me.isLoading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Loading payroll workspace…</div>;
  }

  // Graceful degradation when Philippine Payroll is not enabled for this tenant
  if (!phPayrollEnabled) {
    return (
      <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
        <Card accent elevated style={{ padding: '36px', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            color: '#60a5fa',
            marginBottom: '20px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>

          <h2 style={{ fontSize: '22px', fontWeight: 600, margin: '0 0 8px 0', color: 'var(--color-fg)' }}>
            Philippine Payroll Module Not Licensed
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-muted)', maxWidth: '520px', margin: '0 auto 24px auto', lineHeight: 1.5 }}>
            Automated TRAIN Law tax tables, SSS, PhilHealth, Pag-IBIG statutory calculations, and bank disbursement exports are currently inactive for <strong>{me.data?.organization?.name || 'your company'}</strong>.
          </p>

          <div style={{
            display: 'inline-block',
            padding: '12px 20px',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '6px',
            fontSize: '13px',
            color: 'var(--color-muted)',
            textAlign: 'left',
          }}>
            <div style={{ fontWeight: 600, color: 'var(--color-fg)', marginBottom: '4px' }}>
              How to enable this feature:
            </div>
            <div>
              1. Contact your platform account administrator or platform provider.
            </div>
            <div>
              2. Request the <strong>Philippine Payroll Add-on License</strong> to be activated in the Superadmin Console.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const selectedRun = runDetailQuery.data?.run || runs[0];
  const lineItems = runDetailQuery.data?.lineItems || [];

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '28px',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 600, margin: 0, letterSpacing: '-0.02em' }}>
              Philippine Payroll & Statutory Engine
            </h1>
            <Tag tone="accent">RA 10963 TRAIN Compliant</Tag>
          </div>
          <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: 'var(--color-muted)' }}>
            Bi-monthly quindena payroll processing, multi-branch statutory compliance, and bank disbursement files.
          </p>
        </div>

        {/* Branch Filter & Run Action */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '13px', color: 'var(--color-muted)', fontWeight: 500 }}>
            Branch:
          </label>
          <select
            className="input"
            style={{ width: 'auto' }}
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
          >
            <option value="all">All Branches (Consolidated)</option>
            {branchesQuery.data?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.code})
              </option>
            ))}
          </select>

          <button
            className="btn btn-primary"
            onClick={() => setShowComputeModal(true)}
          >
            + Run New Payroll
          </button>
        </div>
      </div>

      {/* Statutory Regulatory Banners */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <div style={{
          padding: '12px 16px',
          borderRadius: '6px',
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
        }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--color-muted)', fontWeight: 600 }}>
            BIR TRAIN Withholding
          </div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-fg)', marginTop: '4px' }}>
            2023–2026 Tax Table
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '2px' }}>
            0% below ₱20,833/mo · Graduated 15%–35%
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          borderRadius: '6px',
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
        }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--color-muted)', fontWeight: 600 }}>
            SSS & WISP Mandatory
          </div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-fg)', marginTop: '4px' }}>
            14% Contribution Rate
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '2px' }}>
            EE: 4.5% · ER: 9.5% · MSC Cap ₱35,000
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          borderRadius: '6px',
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
        }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--color-muted)', fontWeight: 600 }}>
            PhilHealth (Universal HC)
          </div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-fg)', marginTop: '4px' }}>
            5.0% Premium Rate
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '2px' }}>
            EE: 2.5% · ER: 2.5% · Floor ₱10k, Ceiling ₱100k
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          borderRadius: '6px',
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
        }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--color-muted)', fontWeight: 600 }}>
            Pag-IBIG / HDMF
          </div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-fg)', marginTop: '4px' }}>
            ₱200 EE / ₱200 ER
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '2px' }}>
            Circular 460 · MFS Cap ₱10,000
          </div>
        </div>
      </div>

      {/* Runs Selector Dropdown if runs exist */}
      {runs.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          padding: '12px 16px',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Selected Cut-off Run:</span>
          <select
            className="input"
            style={{ width: 'auto', minWidth: '320px' }}
            value={currentRunId || ''}
            onChange={(e) => setActiveRunId(e.target.value)}
          >
            {runs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title} ({r.code}) — {r.status.toUpperCase()}
              </option>
            ))}
          </select>
          {selectedRun && (
            <Tag tone={selectedRun.status === 'disbursed' || selectedRun.status === 'approved' ? 'accent' : 'neutral'}>
              {selectedRun.status.toUpperCase()}
            </Tag>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--color-border)', marginBottom: '24px' }}>
        <button
          onClick={() => setActiveTab('run')}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'run' ? '2px solid var(--color-accent)' : '2px solid transparent',
            color: activeTab === 'run' ? 'var(--color-accent)' : 'var(--color-muted)',
          }}
        >
          Payroll Register Summary
        </button>
        <button
          onClick={() => setActiveTab('statutory')}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'statutory' ? '2px solid var(--color-accent)' : '2px solid transparent',
            color: activeTab === 'statutory' ? 'var(--color-accent)' : 'var(--color-muted)',
          }}
        >
          Government Statutory Remittances (SSS, PHIC, HDMF, BIR)
        </button>
        <button
          onClick={() => setActiveTab('disbursement')}
          style={{
            padding: '10px 16px',
            border: 'none',
            background: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            borderBottom: activeTab === 'disbursement' ? '2px solid var(--color-accent)' : '2px solid transparent',
            color: activeTab === 'disbursement' ? 'var(--color-accent)' : 'var(--color-muted)',
          }}
        >
          Bank Disbursement Batch Exports
        </button>
      </div>

      {/* Tab 1: Current Payroll Run */}
      {activeTab === 'run' && (
        <>
          {selectedRun ? (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
              }}>
                <Card>
                  <div className="card-kicker">Gross Payroll (PHP)</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, marginTop: '4px' }}>
                    ₱ {Number(selectedRun.totalGross || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-muted" style={{ fontSize: '11px' }}>
                    {selectedRun.employeeCount} Employees ({selectedRun.runType})
                  </div>
                </Card>

                <Card>
                  <div className="card-kicker">BIR Withholding Tax</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, marginTop: '4px' }}>
                    ₱ {Number(selectedRun.totalWithholdingTax || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-muted" style={{ fontSize: '11px' }}>TRAIN Law Withholding</div>
                </Card>

                <Card>
                  <div className="card-kicker">Statutory Deductions (EE)</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, marginTop: '4px' }}>
                    ₱ {(
                      Number(selectedRun.totalSssEe || 0) +
                      Number(selectedRun.totalPhilhealthEe || 0) +
                      Number(selectedRun.totalPagibigEe || 0)
                    ).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-muted" style={{ fontSize: '11px' }}>SSS, PhilHealth, HDMF</div>
                </Card>

                <Card>
                  <div className="card-kicker">Total Net Pay Disbursable</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, marginTop: '4px', color: '#38bdf8' }}>
                    ₱ {Number(selectedRun.totalNetPay || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-muted" style={{ fontSize: '11px' }}>Payout Date: {selectedRun.payoutDate}</div>
                </Card>
              </div>

              <div style={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                overflow: 'hidden',
              }}>
                <div style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid var(--color-border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>
                    {selectedRun.title} ({selectedRun.periodStart} to {selectedRun.periodEnd})
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {selectedRun.status === 'computed' && (
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '12px' }}
                        onClick={() => approveMutation.mutate(selectedRun.id)}
                        disabled={approveMutation.isPending}
                      >
                        Approve Payroll Run
                      </button>
                    )}
                    {selectedRun.status === 'approved' && (
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '12px' }}
                        onClick={() => disburseMutation.mutate(selectedRun.id)}
                        disabled={disburseMutation.isPending}
                      >
                        Mark as Disbursed
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)' }}>
                        <th style={{ padding: '10px 16px' }}>Emp No</th>
                        <th style={{ padding: '10px 16px' }}>Employee Name</th>
                        <th style={{ padding: '10px 16px' }}>Branch</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>Basic Salary</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>De Minimis</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>Gross Pay</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>SSS (EE)</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>PhilHealth</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>Pag-IBIG</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>Withholding Tax</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>Net Take-Home</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((row) => (
                        <tr key={row.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '12px 16px', fontWeight: 600 }}>{row.employeeNo}</td>
                          <td style={{ padding: '12px 16px' }}>{row.lastName}, {row.firstName}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--color-muted)' }}>{row.branchName || 'Head Office'}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>₱ {Number(row.basic_pay).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>₱ {Number(row.de_minimis_allowances).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>₱ {Number(row.gross_pay).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>₱ {Number(row.sss_ee).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>₱ {Number(row.philhealth_ee).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>₱ {Number(row.pagibig_ee).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>₱ {Number(row.withholding_tax).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#38bdf8' }}>
                            ₱ {Number(row.net_pay).toFixed(2)}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <Tag tone={row.disbursement_status === 'paid' ? 'accent' : 'neutral'}>
                              {row.disbursement_status.toUpperCase()}
                            </Tag>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <Card style={{ padding: '40px', textAlign: 'center' }}>
              <p style={{ color: 'var(--color-muted)', marginBottom: '16px' }}>
                No payroll runs found yet. Click below to compute your first Philippine payroll cut-off.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setShowComputeModal(true)}
              >
                + Compute First Quindena Run
              </button>
            </Card>
          )}
        </>
      )}

      {/* Tab 2: Statutory Contributions */}
      {activeTab === 'statutory' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
          <Card kicker="Social Security System" title="Monthly SSS R-3 Contribution Report">
            <p className="card-body" style={{ fontSize: '13px' }}>
              Includes Employee (4.5%) and Employer (9.5%) shares plus WISP mandatory provident fund for salaries exceeding ₱20,000.
            </p>
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => downloadStatutory('sss')}
                disabled={!currentRunId}
              >
                Download SSS R-3 File
              </button>
            </div>
          </Card>

          <Card kicker="Philippine Health Insurance Corporation" title="PhilHealth EPRS Monthly Remittance">
            <p className="card-body" style={{ fontSize: '13px' }}>
              Accurate 5.0% premium split evenly 50/50 between employee and employer, formatted for the Electronic Premium Reporting System.
            </p>
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => downloadStatutory('philhealth')}
                disabled={!currentRunId}
              >
                Download PhilHealth EPRS
              </button>
            </div>
          </Card>

          <Card kicker="Home Development Mutual Fund" title="Pag-IBIG MCRF Remittance Report">
            <p className="card-body" style={{ fontSize: '13px' }}>
              Monthly Contribution Remittance File complying with HDMF online employer portal specifications.
            </p>
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => downloadStatutory('pagibig')}
                disabled={!currentRunId}
              >
                Download Pag-IBIG MCRF
              </button>
            </div>
          </Card>

          <Card kicker="Bureau of Internal Revenue" title="BIR Form 1601-C & 2316 Monthly Summary">
            <p className="card-body" style={{ fontSize: '13px' }}>
              Monthly remittance return of income taxes withheld on compensation, tracking de minimis exemptions and TRAIN brackets.
            </p>
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => downloadStatutory('bir')}
                disabled={!currentRunId}
              >
                Download BIR 1601-C Summary
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Tab 3: Disbursement */}
      {activeTab === 'disbursement' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Card title="Bank Disbursement Batch Exports">
            <p className="card-body" style={{ fontSize: '13px' }}>
              Export compliant batch credit files directly formatted for Philippine commercial banking corporate portals.
            </p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary"
                onClick={() => downloadDisbursement('bpi')}
                disabled={!currentRunId}
              >
                Export BPI BizLink (.csv)
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => downloadDisbursement('bdo')}
                disabled={!currentRunId}
              >
                Export BDO PayLink (.csv)
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => downloadDisbursement('metrobank')}
                disabled={!currentRunId}
              >
                Export Metrobank Direct (.csv)
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => downloadDisbursement('unionbank')}
                disabled={!currentRunId}
              >
                Export UnionBank The Portal (.csv)
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Compute Modal */}
      {showComputeModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
              Compute Quindena Payroll Run
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-muted)', marginBottom: '4px' }}>
                  Payroll Run Title
                </label>
                <input
                  type="text"
                  className="input"
                  style={{ width: '100%' }}
                  value={computeForm.title}
                  onChange={(e) => setComputeForm({ ...computeForm, title: e.target.value })}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-muted)', marginBottom: '4px' }}>
                  Batch Code
                </label>
                <input
                  type="text"
                  className="input"
                  style={{ width: '100%' }}
                  value={computeForm.code}
                  onChange={(e) => setComputeForm({ ...computeForm, code: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-muted)', marginBottom: '4px' }}>
                    Period Start
                  </label>
                  <input
                    type="date"
                    className="input"
                    style={{ width: '100%' }}
                    value={computeForm.periodStart}
                    onChange={(e) => setComputeForm({ ...computeForm, periodStart: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-muted)', marginBottom: '4px' }}>
                    Period End
                  </label>
                  <input
                    type="date"
                    className="input"
                    style={{ width: '100%' }}
                    value={computeForm.periodEnd}
                    onChange={(e) => setComputeForm({ ...computeForm, periodEnd: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-muted)', marginBottom: '4px' }}>
                  Payout Date
                </label>
                <input
                  type="date"
                  className="input"
                  style={{ width: '100%' }}
                  value={computeForm.payoutDate}
                  onChange={(e) => setComputeForm({ ...computeForm, payoutDate: e.target.value })}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-muted)', marginBottom: '4px' }}>
                  Branch
                </label>
                <select
                  className="input"
                  style={{ width: '100%' }}
                  value={computeForm.branchId}
                  onChange={(e) => setComputeForm({ ...computeForm, branchId: e.target.value })}
                >
                  <option value="">All Branches (Consolidated)</option>
                  {branchesQuery.data?.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowComputeModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => computeMutation.mutate(computeForm)}
                disabled={computeMutation.isPending}
              >
                {computeMutation.isPending ? 'Computing...' : 'Calculate & Generate Run'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
